import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import StreamingService from "../../services/StreamingService";
import { toast } from "react-toastify";

// Atualizado conforme lista fornecida
const successStatuses = ["created_hiplade", "running"];
const warningStatuses = ["processing_recording"];
const errorStatuses = [
  "error_create_streaming_hiplade",
  "error_hiplade_transmition_method",
  "error_hiplade_useffmpeg",
  "error_gcp_create_streaming",
  "error_gcp_provision",
  "error_gcp_rca",
  "error_gcp_create_streaming",
  "transcode_failed"
];

// Função utilitária para cor do status
const getStatusColor = (status: string): string => {

  if (successStatuses.includes(status)) return "#43a047"; // verde
  if (warningStatuses.includes(status)) return "#ffa726"; // laranja
  if (errorStatuses.includes(status)) return "#e53935"; // vermelho
  return "#757575"; // cinza padrão
};

const getStatusText = (status: string): string => {
  const statusTexts: { [key: string]: string } = {
    initializing: "Inicializando",
    in_simulation: "Em simulação",
    created_hiplade: "Criado no Hiplade. Aguardando iniciar transmissão...",
    processing_recording: "Processando gravação",
    error_create_streaming_hiplade: "Erro ao criar no Hiplade",
    error_hiplade_transmition_method: "Erro no método de transmissão do Hiplade",
    error_hiplade_useffmpeg: "Erro no uso do FFmpeg pelo Hiplade",
    error_gcp_create_streaming: "Erro ao criar streaming na GCP",
    error_gcp_provision: "Erro na provisão de recursos na GCP",
    transcode_failed: "Erro ao processar o video",
    running: "Ao vivo",
  };
  return statusTexts[status] || status;
}

interface DebriefingStatusWatcherProps {
  status: string;
  debriefingId: any; // Troca stationId/userId por debriefingId
  intervalMs?: number;
  onStatusChange?: (newStatus: any) => void;
}

const DebriefingStatusWatcher: React.FC<DebriefingStatusWatcherProps> = ({
  status,
  debriefingId,
  intervalMs = 4000,
  onStatusChange,
}) => {
  const [checking, setChecking] = useState(false);
  const [cameraStatuses, setCameraStatuses] = useState<any[]>([]);
  const [intervalo, setIntervalo] = useState<number>(intervalMs);

  //console.log("DebriefingStatusWatcher render - status:", status, "debriefingId:", debriefingId);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (status === "initializing" || status === "in_simulation" || status === "running" || status === "created_hiplade" || status === "processing_recording") {
      setChecking(true);
      if (status === "processing_recording" || status === "running") {
        setIntervalo(15000); // Aumenta intervalo para 10 segundos durante processamento de gravação ou simulação rodando, pois mudanças são menos frequentes
      } else {
        setIntervalo(intervalMs); // Usa intervalo padrão para outros status
      }

      interval = setInterval(async () => {
        try {
          await StreamingService.getStreamingsStatusByDebriefing(debriefingId)
            .then((response: any) => {
              //console.log("DebriefingStatusWatcher - resposta do streaming status:", response);

              const streamings = Array.isArray(response.data.streamings) ? response.data.streamings : [];

              setCameraStatuses(streamings);

              //console.log("DebriefingStatusWatcher - streamings encontrados:", streamings);
              /**
               * Se nenhum streaming for encontrado, isso pode indicar um problema na criação do streaming ou que ele ainda não foi criado.
               */
              if(streamings.length === 0) {
                toast.error("Não foi possível iniciar o streaming. Verifique a configuração e tente novamente.");
                //console.log("DebriefingStatusWatcher - Nenhum streaming encontrado para o id: ", debriefingId);
                //window.location.reload();
                setChecking(false);
                setCameraStatuses([]);
                onStatusChange && onStatusChange(null);
              }

              if(streamings.some((s: any) => errorStatuses.includes(s.status))) {
                toast.error("Erro ao iniciar streaming. Verifique a configuração e tente novamente.");
                setChecking(false);
                setCameraStatuses([]);
                onStatusChange && onStatusChange(null);
              }

              if (streamings.some((s: any) => s.status === "finished")) {
                window.location.reload();
                setChecking(false);
                onStatusChange && onStatusChange("finished");
              }

              if (streamings.some((s: any) => s.status === "processing_recording")) {
                // window.location.reload();
                // setChecking(false);
                onStatusChange && onStatusChange("processing_recording");
              }


              // Chama onStatusChange se debriefing_status for 'in_simulation' e houver streaming principal 'running'
              if (
                streamings.some((s: any) => s.status === "running" && s.camera_main === 1)
              ) {
                //console.log("DebriefingStatusWatcher detectou streaming principal rodando, atualizando status para 'in_simulation'");
                //setChecking(false);
                //toast.success("Streaming iniciado com sucesso.", { type: "success" });
                onStatusChange && onStatusChange("running");
              }


            });
        } catch (e) {
          // Trate erros se necessário
        }
      }, intervalo);
    } else {

      setChecking(false);
      setCameraStatuses([]);

    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [status, debriefingId, intervalo, onStatusChange]);

  if (!checking) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', ml: 2 }}>
      {cameraStatuses.length === 0 ? (
        <Typography variant="caption" color="textSecondary">
          Consultando status dos streamings...
        </Typography>
      ) : (
        <Typography variant="caption" color="textSecondary">
          {cameraStatuses.map((cam, idx) => (
            <span key={cam.camera_name || idx} style={{ fontWeight: cam.camera_main === 1 ? 'bold' : 'normal' }}>
              {cam.camera_name ? `${cam.camera_name}: ` : ''} <span style={{ color: getStatusColor(cam.status), fontWeight: 'normal' }}>{getStatusText(cam.status)}</span>
              {idx < cameraStatuses.length - 1 && ' | '}
            </span>
          ))}
        </Typography>
      )}
    </Box>
  );
};

export default DebriefingStatusWatcher;
